import { AnimatePresence, motion } from "framer-motion";
import type React from "react";
import { useLayoutEffect, useRef } from "react";

const FadeAndHeight = (props: {
    show: boolean;
    children: React.ReactNode;
    options?: {
        preventInitialAnimation?: boolean;
        speed?: "default" | "fast" | "slow";
    };
}) => {
    const options = props.options || {};
    const inHeightDuration =
        options.speed === "fast" ? 0.2 : options.speed === "slow" ? 0.5 : 0.3;
    const inOpacityDuration =
        options.speed === "fast" ? 0.3 : options.speed === "slow" ? 0.8 : 0.5;
    const exitDuration =
        options.speed === "fast" ? 0.2 : options.speed === "slow" ? 0.5 : 0.3;

    // Add references to track and preserve scroll position
    const elementRef = useRef<HTMLDivElement>(null);
    const scrollContainerRef = useRef<HTMLElement | null>(null);
    const scrollPositionRef = useRef(0);

    // Find the scrollable parent and preserve its scroll position during animations
    useLayoutEffect(() => {
        if (!elementRef.current) return;

        // Find the first scrollable parent
        const findScrollableParent = (
            node: HTMLElement | null,
        ): HTMLElement | null => {
            if (!node || node === document.body) return null;

            const { overflowY, overflowX } = window.getComputedStyle(node);
            const isScrollable =
                (overflowY === "auto" ||
                    overflowY === "scroll" ||
                    overflowX === "auto" ||
                    overflowX === "scroll") &&
                (node.scrollHeight > node.clientHeight ||
                    node.scrollWidth > node.clientWidth);

            return isScrollable
                ? node
                : findScrollableParent(node.parentElement);
        };

        // Store reference to scrollable container
        scrollContainerRef.current = findScrollableParent(
            elementRef.current.parentElement,
        );

        // Store current scroll position
        if (scrollContainerRef.current) {
            scrollPositionRef.current = scrollContainerRef.current.scrollTop;
        }
    }, [props.show]);

    // When height changes, maintain scroll position
    useLayoutEffect(() => {
        if (
            props.show &&
            scrollContainerRef.current &&
            typeof scrollPositionRef.current === "number"
        ) {
            requestAnimationFrame(() => {
                if (scrollContainerRef.current) {
                    scrollContainerRef.current.scrollTop =
                        scrollPositionRef.current;
                }
            });
        }
    }, [props.show]);

    return (
        <AnimatePresence mode="wait">
            {props.show ? (
                <motion.div
                    ref={elementRef}
                    initial={
                        options.preventInitialAnimation
                            ? { opacity: 1, height: "auto" }
                            : { opacity: 0, height: 0 }
                    }
                    animate={{
                        opacity: 1,
                        height: "auto",
                        transition: {
                            height: {
                                type: "tween",
                                ease: "easeInOut",
                                duration: inHeightDuration,
                            },
                            opacity: {
                                duration: inOpacityDuration,
                                delay: 0.1,
                            },
                        },
                    }}
                    exit={{
                        opacity: 0,
                        height: 0,
                        transition: {
                            height: {
                                type: "tween",
                                ease: "easeInOut",
                                duration: exitDuration,
                            },
                            opacity: { duration: exitDuration },
                        },
                    }}
                    style={{ overflow: "hidden", transformOrigin: "top" }}
                    onLayoutAnimationStart={() => {
                        // Store current scroll position at the start of any layout animation
                        if (scrollContainerRef.current) {
                            scrollPositionRef.current =
                                scrollContainerRef.current.scrollTop;
                        }
                    }}
                    onAnimationComplete={() => {
                        // Restore scroll position when animation completes
                        if (scrollContainerRef.current && props.show) {
                            scrollContainerRef.current.scrollTop =
                                scrollPositionRef.current;
                        }
                    }}>
                    {props.children}
                </motion.div>
            ) : null}
        </AnimatePresence>
    );
};

export { FadeAndHeight };
